import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, List, ListItem } from "@mui/material";
import {
  LoadCanvasTemplateNoReload,
  loadCaptchaEnginge,
  validateCaptcha,
} from "react-simple-captcha";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Update } from "@mui/icons-material";

import {
  authenticate,
  contactSupport,
  authAppCodeSubtitle,
  gAuthGuideline,
  authAppSteps,
  next,
  setupAuthAppMsg,
  setupAuthenticatorApp,
  verificationCode,
  captchaCode,
  captchaSubHeader,
  captchaCodePlaceholder,
  invalidCaptchaCode,
  verify,
  verifyingHuman,
} from "../../messages";
import Text from "../../text/Text";
import { Input } from "../../input/Input";
import Clipboard from "../../clipboard/Clipboard";
import Button from "../../button/Button";
import {
  callAPIInterface,
  captchaBackgroundColor,
  captchaDigit,
  handleContactSupport,
} from "../../constants";
import { OTPInput } from "../../otpInput/OTPInput";
import { CustomLink } from "../../link/Link";
import CommonDrawer from "../../drawer/CommonDrawer";
import CustomDivider from "../../divider/Divider";

const qrSize = 254;

const GoogleAuthenticatorDrawer = ({
  openModal,
  setOpenModal,
  gAuthConfig,
  getMfaStatus,
  gAuthSetup,
}) => {
  const [step, setStep] = useState(1);
  const [captcha, setCaptcha] = useState("");
  const [isInvalidCaptcha, setIsInvalidCaptcha] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState(null);

  const isGAuthEnabled = gAuthConfig?.is_google_auth_enabled;

  const modalHeader = useMemo(() => {
    switch (step) {
      case 1:
        return verifyingHuman;
      case 2:
        return setupAuthenticatorApp;
      case 3:
        return verificationCode;
      default:
        return "";
    }
  }, [step]);

  const renderModalFooter = useCallback(() => {
    switch (step) {
      case 1:
        return (
          <Button
            loading={isSubmitting}
            variant="contained"
            sx={{
              borderRadius: "4px !important",
              width: "100%",
            }}
            label={verify}
            onClick={handleVerifyCaptcha}
          />
        );
      case 2: {
        return (
          gAuthSetup && (
            <Button
              fullWidth
              label={next}
              onClick={() => {
                setStep(3);
              }}
              variant="contained"
              color="primary"
            />
          )
        );
      }
      case 3:
        return (
          <Button
            loading={isSubmitting}
            variant="contained"
            sx={{
              borderRadius: "4px !important",
              textTransform: "capitalize",
            }}
            fullWidth
            disabled={otp.length !== 6}
            label={authenticate}
            onClick={handleVerifyCode}
          />
        );
      default:
        return <></>;
    }
  }, [gAuthSetup, step, isSubmitting, error, otp, captcha, isInvalidCaptcha]);

  const renderModalBody = useCallback(() => {
    switch (step) {
      case 1:
        return (
          <Box>
            <Text className="heading-title">{modalHeader}</Text>
            <Text className="heading-subtitle">{captchaSubHeader}</Text>
            <Box className="captcha-container-box" margin="20px 0">
              <LoadCanvasTemplateNoReload />
            </Box>
            <Box className="reload-captcha">
              <Text onClick={handleReloadCaptcha} font="bold" fontSize={14}>
                <Update />
                Reload Captcha
              </Text>
            </Box>
            <Input
              fullWidth
              showLabel
              value={captcha}
              label={captchaCode}
              onChange={(e) => {
                setIsInvalidCaptcha(false);
                setCaptcha(e.target.value);
              }}
              type="text"
              inputProps={{ maxLength: captchaDigit }}
              customClass="mt-24"
              placeholder={captchaCodePlaceholder}
            />
            {isInvalidCaptcha && (
              <Text
                className="error-text"
                size={16}
                font="regular"
                sx={{ textAlign: "left", marginTop: "5px" }}
              >
                {invalidCaptchaCode}
              </Text>
            )}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Text className="heading-title">{modalHeader}</Text>
            <Text className="heading-subtitle">{setupAuthAppMsg}</Text>
            <Box className="gauth-modal-container">
              <List
                sx={{
                  listStyle: "decimal",
                  pl: 4,
                  "& li::marker": {
                    fontSize: "14px",
                  },
                }}
              >
                {authAppSteps.map((step, idx) => (
                  <ListItem key={idx} sx={{ display: "list-item" }}>
                    <Text size={14}>{step}</Text>
                  </ListItem>
                ))}
              </List>
              <img
                src={gAuthSetup?.otp_auth_url}
                height={qrSize}
                width={qrSize}
                sx={{ alignSelf: "center" }}
                alt="Google Authenticator QR Code for MFA setup"
                onError={(e) => {
                  e.target.style.display = "none";
                  setError(new Error("Failed to load QR code"));
                }}
                className="google-auth-qr-img"
              />
              <Box className="google-auth-code-box">
                <Box className="code-box-label">
                  <Text className="scroll-text">{gAuthSetup?.secret_key}</Text>
                </Box>
                <Clipboard
                  text={gAuthSetup?.secret_key}
                  height="18px"
                  width="18px"
                />
              </Box>
            </Box>
            <Text className="grey-text mb-10" size={12} font="regular">
              {gAuthGuideline}
            </Text>
          </Box>
        );
      case 3:
        return (
          <Box>
            <Text className="heading-title">{modalHeader}</Text>
            <Text className="heading-subtitle"> {authAppCodeSubtitle}</Text>
            <OTPInput
              value={otp}
              onChange={(val) => setOtp(val)}
              numInputs={6}
              containerStyle={{
                marginTop: "20px",
                gap: "3%",
              }}
            />
            <Box textAlign="center" marginTop="20px">
              <CustomLink
                sx={{
                  fontWeight: 600,
                }}
                onClick={handleContactSupport}
                color="primary"
              >
                {contactSupport}
              </CustomLink>
            </Box>
          </Box>
        );
      default:
        return <></>;
    }
  }, [gAuthSetup, step, isSubmitting, error, otp, captcha, isInvalidCaptcha]);

  const handleVerifyCaptcha = () => {
    setIsSubmitting(true);
    const isValid = validateCaptcha(captcha);
    if (isValid) {
      setIsInvalidCaptcha(false);
      if (isGAuthEnabled) {
        setStep(3);
      } else {
        setStep(2);
      }
    } else {
      setIsInvalidCaptcha(true);
    }
    setIsSubmitting(false);
  };

  const handleReloadCaptcha = () => {
    loadCaptchaEnginge(captchaDigit, captchaBackgroundColor);
    setCaptcha("");
  };

  const handleVerifyCode = () => {
    setIsSubmitting(true);

    const mfaEndPoint = isGAuthEnabled
      ? "/mfa/google-auth/disable"
      : "/mfa/google-auth/verify";

    callAPIInterface("POST", mfaEndPoint, { otp })
      .then(async () => {
        await getMfaStatus();
        setOpenModal(false);
      })
      .catch((err) => {
        console.error("Error while verifying code", err);
      })
      .finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    setTimeout(() => {
      loadCaptchaEnginge(captchaDigit, captchaBackgroundColor);
    }, 0);
  }, []);

  const handleModalClose = () => {
    setStep(1);
    setCaptcha("");
    setOtp("");
    setIsInvalidCaptcha(false);
    setError(null);
    setOpenModal(false);
  };

  return (
    <CommonDrawer
      anchor="bottom"
      open={openModal}
      onClose={handleModalClose}
      className="two-factor-drawer"
    >
      <Box className="add-contact-drawer-content">
        <Box className="drawer-header">
          <ArrowBackIcon onClick={handleModalClose}></ArrowBackIcon>
        </Box>
        <Box className="drawer-body">{renderModalBody()}</Box>
        <CustomDivider />
        <Box className="drawer-footer">{renderModalFooter()}</Box>
      </Box>
    </CommonDrawer>
  );
};

export default GoogleAuthenticatorDrawer;
